<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useDeviceStore } from '@/stores/device'
import { useRoute } from 'vue-router'
import Sidebar from './Sidebar.vue'
import Header from './Header.vue'
import BottomMenu from './BottomMenu.vue'
import NavPopup from './NavPopup.vue'
import NoisePanel from '@/components/noise/NoisePanel.vue'
import HotkeyDialog from '@/components/hotkey/HotkeyDialog.vue'
import { useEqStore } from '@/stores/eq'
import { useChijiStore } from '@/stores/chiji'
import { useHotkeys, type HotkeyAction } from '@/composables/useHotkeys'
import { ElMessage, ElMessageBox } from 'element-plus'
import { useI18n } from 'vue-i18n'

const deviceStore = useDeviceStore()
const eqStore = useEqStore()
const chijiStore = useChijiStore()
const route = useRoute()
const { t } = useI18n()
const footerPreview = import.meta.env.DEV && new URLSearchParams(window.location.search).has('footerPreview')
const props = withDefaults(
  defineProps<{
    hideSidebar?: boolean
    headerAlwaysVisible?: boolean
  }>(),
  {
    hideSidebar: false,
    headerAlwaysVisible: false,
  }
)

const isSidebarCollapsed = ref(false)

const activePopup = ref<string | null>(null)
const showHotkeyDialog = ref(false)

const sidebarWidth = computed(() => (props.hideSidebar || isSidebarCollapsed.value ? '0px' : '62px'))

const showBottomMenu = computed(() => {
  return footerPreview || (deviceStore.isConnected && route.path === '/')
})
const isFullscreenFeature = computed(() => route.path === '/eq' || route.path === '/chiji')

const toggleSidebar = () => {
  isSidebarCollapsed.value = !isSidebarCollapsed.value
}

const togglePopup = (popupId: string) => {
  activePopup.value = activePopup.value === popupId ? null : popupId
}

const closePopup = () => {
  activePopup.value = null
}

const openHotkeyDialog = () => {
  showHotkeyDialog.value = true
}

const closeHotkeyDialog = () => {
  showHotkeyDialog.value = false
}

const togglePowerByHotkey = async () => {
  if (deviceStore.connectionType === 'USB') {
    ElMessage({ message: 'Ineffective', duration: 1200 })
    return
  }

  if (!deviceStore.canUsePowerControl) return

  if (deviceStore.isPowerOff) {
    ElMessage({
      message: t('footer.poweringOn', 'Powering on...') as string,
      duration: 4500,
    })
    await deviceStore.setPowerOn(true)
    return
  }

  try {
    await ElMessageBox.confirm(
      `
        <div style="text-align: left;">
          <div style="font-size: 16px;margin-bottom: 8px;">${t('footer.powerOffConfirmTitle', 'Are you sure you want to power off the device?')}</div>
          <div style="font-size: 14px; color:rgba(255, 255, 255, 0.75);">${t('footer.powerOffConfirmDesc', 'After powering off, the device will enter a weak connection state and consume less power.')}</div>
        </div>
      `,
      '',
      {
        confirmButtonText: t('footer.powerOffConfirmBtn', 'Power Off') as string,
        cancelButtonText: t('footer.cancel', 'Cancel') as string,
        dangerouslyUseHTMLString: true,
        showClose: false,
        closeOnClickModal: false,
        closeOnPressEscape: true,
      },
    )
  } catch {
    return
  }

  await deviceStore.setPowerOn(false)
}

const handleHotkeyAction = async (action: HotkeyAction) => {
  switch (action) {
    case 'powerToggle':
      await togglePowerByHotkey()
      break
    case 'eqToggle':
      await eqStore.setEqEnabled(!eqStore.isEnabled)
      break
    case 'rgbToggle':
      await deviceStore.setRgbEnabled(!deviceStore.rgbEnabled)
      break
    case 'aiToggle':
      await deviceStore.setNoiseEnabled(!deviceStore.noiseEnabled)
      break
    case 'balenceToggle':
      await deviceStore.setAudioSplit(!deviceStore.audioSplitEnabled)
      break
    case 'Positioning':
      await chijiStore.setEnabled(!chijiStore.enabled)
      break
  }
}

const isHotkeyActionAvailable = (action: HotkeyAction) => {
  if (!deviceStore.isConnected || !deviceStore.isDeviceConnected) return false
  if (route.path !== '/') return false

  switch (action) {
    case 'powerToggle':
      return deviceStore.canUsePowerControl
    case 'eqToggle':
      return deviceStore.canUseEq
    case 'rgbToggle':
      return deviceStore.canUseLed
    case 'aiToggle':
      return deviceStore.canUseNoiseReduction
    case 'balenceToggle':
    case 'Positioning':
      return deviceStore.canUseSpecialAudio
    default:
      return true
  }
}

const { hotkeys, setHotkey, resetHotkeys, findConflict } = useHotkeys({
  enabled: computed(() => deviceStore.canUseHotkey),
  settingOpen: showHotkeyDialog,
  onTrigger: handleHotkeyAction,
  isActionAvailable: isHotkeyActionAvailable,
})

watch(
  () => route.path,
  () => {
    // 对齐旧版：切换到任何页面时，关闭所有左侧popup（避免右侧同时存在多个）
    activePopup.value = null
  }
)
</script>

<template>
  <div class="main-layout" :style="{ '--sidebar-width': sidebarWidth }">
    <Sidebar
      v-if="!props.hideSidebar"
      :collapsed="isSidebarCollapsed"
      :active-popup="activePopup"
      @toggle="toggleSidebar"
      @toggle-popup="togglePopup"
      @close-popup="closePopup"
    />
    <Header v-if="!isFullscreenFeature" :always-visible="props.headerAlwaysVisible" @open-hotkey="openHotkeyDialog" />
    <main class="main-content" :class="{ 'has-bottom-menu': !props.hideSidebar && deviceStore.isConnected, fullscreen: isFullscreenFeature }">
      <slot />
    </main>

    <NavPopup
      v-if="!props.hideSidebar"
      :visible="activePopup === 'noise'"
      title=""
      :fullscreen="false"
      :hide-header="true"
      @close="closePopup"
    >
      <NoisePanel @close="closePopup" />
    </NavPopup>

    <BottomMenu v-if="!props.hideSidebar && showBottomMenu" />

    <HotkeyDialog
      :visible="showHotkeyDialog"
      :hotkeys="hotkeys"
      :find-conflict="findConflict"
      @close="closeHotkeyDialog"
      @reset="resetHotkeys"
      @set-hotkey="setHotkey"
    />
  </div>
</template>

<style scoped lang="scss">
.main-layout {
  min-height: 100vh;
  background: linear-gradient(to bottom, #090a10, #171922);
}

.main-content {
  // Sidebar is a fixed overlay. Keep the content viewport full-width so
  // collapsing/expanding the sidebar never changes the visual center.
  margin-left: 0;
  margin-top: 60px;
  height: calc(100vh - 60px);
  padding: 0;

  &.has-bottom-menu {
    padding-bottom: 100px;
  }

  &.fullscreen {
    // EQ / Positioning 属于整页功能页：侧边栏展开时仍需给它预留实际宽度，
    // 否则固定定位的 Sidebar 会直接盖住页面左侧标题、标签和控制项。
    // 首页不使用这个 margin，因此首页主图仍以整屏为基准居中，不会随侧边栏开关左右移动。
    margin-left: var(--sidebar-width);
    margin-top: 0;
    height: 100vh;
    padding-bottom: 0;
  }
}
</style>
